import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import lodashValues from 'lodash/fp/values';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_APARTMENT_LOCATOR,
  USER_ROLE_HOUSEHOLD_ADMIN,
  USER_ROLE_LIMITED_TOURS,
  USER_ROLE_AGENT,
  USER_ROLE_PARTNER,
  ALL_USER_ROLES,
} from 'common/userRoles';
import {
  LEAD_STEP_STATUS_LIST,
  APT_LEAD_ASSIGNMENT_CONTEXT_SUNROOM_TEAM_DIRECT,
  APT_LEAD_ASSIGNMENT_CONTEXT_SUNROOM_TEAM_ROUNDROBIN,
  APT_LEAD_ASSIGNMENT_DISPLAY_CONTEXT_SUNROOM_TEAM,
} from 'common/leadConstants';
import {
  leaseSituationTagMap,
  FRONT_TAG_LIMITED_TOURS,
} from 'common/frontConstants';

import Navbar from 'client/components/Navbar';
import ButtonDeprecated, {
  BUTTON_THEME_BLUE,
  BUTTON_THEME_YELLOW,
} from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

import {
  SR_API_ACTION_GET_USER_BY_PHONE,
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_ADMIN_UPDATE_ASSIGNED_APARTMENT_LOCATOR,
  SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR,
  SR_API_ACTION_UPDATE_RENTER_FRONT_TAGS,
  SR_API_ACTION_APARTMENT_LOCATOR_UPDATE_LEAD_STATUS,
  SR_API_ACTION_HOUSEHOLD_ASSIGN_ADMIN_USER_ID,
  SR_API_ACTION_UPDATE_MOVE_IN_DATE,
  SR_API_ACTION_ADMIN_RESET_HOUSEHOLD_OVERVIEW_SETTINGS,
  SR_API_ACTION_PARTNER_USER_UPSERT,
} from 'api/actionTypes';
import { SCREEN_ID_ADMIN_EDIT_LISTING } from 'client/constants/screenIds';
import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import { validators, createInitialValues } from 'client/helpers/formikHelpers';
import branch from 'client/core/branch';

import AdminActions from 'client/actions/Admin';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import HouseholdActions from 'client/actions/Household';
import MarketActions from 'client/actions/Market';
import PartnerActions from 'client/actions/Partner';

import { leaseSituationShortOptionItems } from 'common/leaseSituationOptionItems';
import extractPhoneFromText from 'common/util/extractPhoneFromText';
import {
  dataTable,
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

function getDataTableSettingsFromProps(props) {
  const user = props.user || {};
  const apartmentLocator = props.apartmentLocator;
  const tableSettings = {
    dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
    count: 1,
  };

  if (user) {
    tableSettings.viewId = `leads-id-${user.id}-${apartmentLocator}`;
    tableSettings.filter = {
      apartmentLocatorUserId: apartmentLocator,
      renterUserId: user.id,
    };
  }

  return tableSettings;
}

function getFormFields({
  includeApartmentLocatorFields,
  includeHouseholdAdminFields,
  includeAgentFields,
  apartmentLocatorsList,
  householdAdminsList,
  homebaseEmployeeList,
  marketList,
  lead,
  householdId,
  partners = [],
  onPartnerChange,
}) {
  const apartmentLocatorChoices = (apartmentLocatorsList || []).map((locator) => ({
    label: locator.name,
    value: parseInt(locator.id, 10),
  }));
  const householdAdminsChoices = (householdAdminsList || []).map((householdAdmin) => ({
    label: householdAdmin.name,
    value: parseInt(householdAdmin.id, 10),
  }));
  const homebaseEmployeeChoices = (homebaseEmployeeList || []).map((homebaseEmployee) => ({
    label: `${homebaseEmployee.firstName} ${homebaseEmployee.lastName}`,
    value: homebaseEmployee.id,
  }));
  const staffMarketChoices = (marketList || []).map((market) => ({
    label: market.displayName,
    value: market.id,
  }));

  const formFields = [
    {
      id: 'name',
      title: 'name',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'email',
      title: 'email',
      type: 'email',
      validations: ['email'],
    },
    {
      id: 'phone',
      title: 'phone',
      type: 'phone',
      validations: ['required', 'phone'],
    },
    {
      id: 'secondaryPhone',
      title: 'secondary phone',
      type: 'phone',
      validations: includeApartmentLocatorFields ? ['required', 'phone'] : [],
    },
    {
      id: 'apartmentLocator',
      title: 'apartment locator',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'Round Robin 🍕',
            value: 'roundRobin',
          },
          ...apartmentLocatorChoices,
        ],
        multipleSelection: false,
        allowEmptyArray: true,
      },
      validations: [],
    },
    {
      id: 'householdAdmin',
      title: 'App Pro',
      type: 'select',
      properties: {
        choices: householdAdminsChoices,
        multipleSelection: false,
        allowEmptyArray: true,
        disabled: !householdId,
        placeholder: !householdId ? 'No household!' : 'Choose...',
      },
      validations: [],
    },
    {
      id: 'moveInDate',
      title: 'move-in date',
      type: 'date_picker',
      properties: {
        placeholder: 'Choose date...',
      },
      validations: ['required'],
    },
    {
      id: 'leaseSituation',
      title: 'lease situation',
      type: 'select',
      properties: {
        choices: leaseSituationShortOptionItems,
        multipleSelection: false,
        allowEmptyArray: false,
      },
      validations: [],
    },
    {
      id: 'partnerId',
      type: 'hidden',
    },
    {
      id: 'partnerDisplayName',
      title: 'assign to partner',
      type: 'autocomplete',
      properties: {
        placeholder: '',
        choices: partners
          .filter(({ invalidSignup }) => invalidSignup === false)
          .map((partner) => {
            return partner.displayName;
          }),
        filterFunc: ({ value, choice }) => {
          const lowerCaseChoice = choice.toLowerCase();
          return value !== lowerCaseChoice && lowerCaseChoice.includes(value);
        },
        onChange: onPartnerChange,
        minCharMatchCount: 1,
      },
    },
    {
      id: 'roles',
      title: 'roles',
      type: 'multiple_choice',
      properties: {
        choices: ALL_USER_ROLES.map((userRole) => ({
          label: userRole,
          value: userRole,
        })),
        multipleSelection: true,
        allowEmptyArray: false,
      },
      validations: ['required'],
    },
    {
      id: 'profileImage',
      title: 'Profile Image',
      type: 'file_single',
      properties: {
        fileAccept: 'image/*',
        filePath: '/user-uploads/profile-images/{uuid}',
      },
    },
  ];

  if (includeApartmentLocatorFields) {
    formFields.push(
      {
        id: 'apartmentLeadsSwitchDisabled',
        title: 'Disable Leads Switch (Override)',
        type: 'checkbox',
      },
      {
        id: 'apartmentLeadsEnabled',
        title: 'Apartment Leads Enabled',
        type: 'checkbox',
      },
      {
        id: 'apartmentLocatorSlackChannel',
        title: 'Apartment Locator Slack Channel',
        type: 'short_text',
        validations: ['required'],
      },
      {
        id: 'apartmentLocatorFrontChannelId',
        title: 'Apartment Locator Front Channel Id',
        type: 'short_text',
      },
      {
        id: 'apartmentLocatorFrontInboxId',
        title: 'Apartment Locator Front Inbox Id',
        type: 'short_text',
      },
      {
        id: 'apartmentLocatorAutoTextEnabled',
        title: 'Apartment Locator Auto-Text Enabled',
        type: 'checkbox',
      },
      {
        id: 'apartmentLocatorAutoTextTemplate',
        title: 'Apartment Locator Auto-Text Template',
        type: 'long_text',
      },
      {
        id: 'apartmentLocatorAutoTextTemplateTwo',
        title: 'Apartment Locator Auto-Text Template Two',
        type: 'long_text',
      },
      {
        id: 'apartmentLocatorAutoTextTemplateThree',
        title: 'Apartment Locator Auto-Text Template Three',
        type: 'long_text',
      },
      {
        id: 'apartmentLocatorAutoTextTemplateFour',
        title: 'Apartment Locator Auto-Text Template Four',
        type: 'long_text',
      },
      {
        id: 'apartmentLocatorAutoTextTemplateFive',
        title: 'Apartment Locator Auto-Text Template Five',
        type: 'long_text',
      },
      {
        id: 'apartmentLocatorSignupEmailTemplateId',
        title: 'Apartment Locator Signup Email TemplateId',
        type: 'short_text',
      },
      {
        id: 'profilePicture',
        title: 'Locator Profile Picture',
        type: 'file_single',
        properties: {
          fileAccept: 'image/*',
          filePath: '/user-uploads/locator-profile-images/{uuid}',
        },
      },
    );
  }

  if (includeApartmentLocatorFields || includeAgentFields) {
    formFields.push(
      {
        id: 'staffMarketId',
        title: 'Staff Market',
        type: 'select',
        properties: {
          choices: staffMarketChoices,
          multipleSelection: false,
          allowEmptyArray: false,
        },
        validations: ['required'],
      }
    );
  }

  if (includeAgentFields) {
    formFields.push(
      {
        id: 'agentProfileImageHack',
        title: 'Tour Guide Profile Link',
        type: 'short_text',
      },
      {
        id: 'homebaseEmployeeId',
        title: 'Homebase Employee',
        type: 'select',
        properties: {
          choices: homebaseEmployeeChoices,
          multipleSelection: false,
          allowEmptyArray: false,
        },
        validations: ['required'],
      },
    );
  }

  if (includeHouseholdAdminFields) {
    formFields.push(
      {
        id: 'frontTeammateId',
        title: 'Front Teammate ID',
        type: 'short_text',
      },
    );
  }

  if (lead) {
    formFields.push(
      {
        id: 'leadStatus',
        title: 'Lead Status',
        type: 'select',
        properties: {
          choices: LEAD_STEP_STATUS_LIST.map((leadStatus) => ({
            label: leadStatus,
            value: leadStatus,
          })),
        },
      },
    );
  }

  return formFields;
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  userApiState: {
    action: SR_API_ACTION_GET_USER_BY_PHONE,
  },
  updateUserApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
  },
  resetHouseholdOverviewSettingsApiState: {
    action: SR_API_ACTION_ADMIN_RESET_HOUSEHOLD_OVERVIEW_SETTINGS,
  },
})
@withScreenId(SCREEN_ID_ADMIN_EDIT_LISTING)
@dataTable(getDataTableSettingsFromProps)
@branch({
  user: ['admin', 'user'],
  apartmentLocators: ['apartmentLocators'],
  homebaseEmployeeList: ['homebaseEmployees'],
  householdAdminUserId: ['admin', 'userHouseholdAdminUserId'],
  householdId: ['admin', 'userHouseholdId'],
  apartmentLocator: ['admin', 'userApartmentLocatorId'],
  householdAdminsList: ['householdAdminsList'],
  marketsById: ['markets', 'byId'],
  partners: ['partners', 'list'],
  partnersByUserId: ['partners', 'byUserId'],
})
class AdminUserEditPage extends Component {
  static propTypes = {
    items: PropTypes.array,
    apartmentLocators: PropTypes.array,
    homebaseEmployeeList: PropTypes.array,
    householdAdminsList: PropTypes.array,
    householdAdminUserId: PropTypes.number,
    householdId: PropTypes.number,
    marketsById: PropTypes.object.isRequired,
    userApiState: PropTypes.object.isRequired,
    updateUserApiState: PropTypes.object.isRequired,
    resetHouseholdOverviewSettingsApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([
      ApartmentLocatorActions.getApartmentLocatorsList(inject),
      HouseholdActions.getHouseholdAdminsList(inject),
      AdminActions.getHomebaseEmployeeList(inject),
      MarketActions.getList(inject),
      PartnerActions.getPartnerList(inject),
    ]);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Admin Edit User | Sunroom Rentals',
    section: 'Admin',
  });

  constructor(props) {
    super(props);

    this.state = {
      isDataLoaded: false,
    };
  }

  _onLoadUser = ({ userPhone }) => {
    const { dispatch } = this.props;

    this.setState({ isDataLoaded: false });

    Promise.all([
      dispatch(AdminActions.getUserByPhone, extractPhoneFromText(userPhone)),
      dispatch(AdminActions.getAssignedHouseholdAdminByPhone, extractPhoneFromText(userPhone)),
      dispatch(HouseholdActions.getHouseholdByUserPhone, extractPhoneFromText(userPhone)),
    ]).then(([user]) => {
      if (user) {
        dispatch(PartnerActions.getByUserId, {
          userId: user.id,
        });

        dispatch(AdminActions.getAssignedApartmentLocatorByPhone, user.phone)
          .then((apartmentLocator) => {
            if (apartmentLocator) {
              const tableSettings = getDataTableSettingsFromProps({
                user: {
                  ...user,
                  apartmentLocator: apartmentLocator.id,
                },
              });

              dispatch(actionDataTableLoadItems, tableSettings).then(() => {
                this.setState({ isDataLoaded: true });
              });
            }
            else {
              this.setState({ isDataLoaded: true });
            }
          });
      }
      else {
        this.setState({ isDataLoaded: true });
      }
    });
  };

  _onUpdateUser = (values) => {
    const { dispatch, user, userApartmentLocator, items, householdId, householdAdminUserId } = this.props;
    const updates = { ...values };

    Object.keys(values).forEach((key) => {
      if (!user[key] && !values[key]) {
        delete updates[key];
      }
    });

    const {
      apartmentLocator,
      householdAdmin,
      moveInDate,
      leadStatus,
      partnerId,
      partnerDisplayName,
      ...userUpdates
    } = updates;

    if (moveInDate && moveInDate !== user.moveInDate) {
      dispatch(postWithState, {
        action: SR_API_ACTION_UPDATE_MOVE_IN_DATE,
        payload: {
          userId: user.id,
          moveInDate: moveInDate,
        },
      });
    }

    if (apartmentLocator && apartmentLocator !== userApartmentLocator) {
      if (apartmentLocator === 'roundRobin') {
        dispatch(postWithState, {
          action: SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR,
          payload: {
            userId: user.id,
            assignmentContext: APT_LEAD_ASSIGNMENT_CONTEXT_SUNROOM_TEAM_ROUNDROBIN,
            displayContext: APT_LEAD_ASSIGNMENT_DISPLAY_CONTEXT_SUNROOM_TEAM,
            enableSmsMessage: false,
            canOverwrite: true,
          },
        });
      }
      else {
        dispatch(postWithState, {
          action: SR_API_ACTION_ADMIN_UPDATE_ASSIGNED_APARTMENT_LOCATOR,
          payload: {
            renterUserId: user.id,
            apartmentLocatorUserId: parseInt(apartmentLocator, 10),
            assignmentContext: APT_LEAD_ASSIGNMENT_CONTEXT_SUNROOM_TEAM_DIRECT,
            displayContext: APT_LEAD_ASSIGNMENT_DISPLAY_CONTEXT_SUNROOM_TEAM,
          },
        });
      }
    }

    if (householdAdmin && householdAdmin !== householdAdminUserId) {
      const adminUserId = parseInt(householdAdmin, 10) || null;
      dispatch(postWithState, {
        action: SR_API_ACTION_HOUSEHOLD_ASSIGN_ADMIN_USER_ID,
        payload: {
          householdId: householdId,
          adminUserId: adminUserId,
        },
      });
    }

    if (partnerId && partnerDisplayName) {
      dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_USER_UPSERT,
        payload: {
          partnerId: partnerId,
          userId: user.id,
        },
      });
    }

    if (leadStatus && leadStatus !== items[0].status && apartmentLocator) {
      dispatch(postWithState, {
        action: SR_API_ACTION_APARTMENT_LOCATOR_UPDATE_LEAD_STATUS,
        payload: {
          apartmentLocatorUserId: parseInt(apartmentLocator, 10),
          renterUserId: user.id,
          leadStatus: leadStatus,
        },
      }).then(() => {
        dispatch(actionDataTableLoadItems, getDataTableSettingsFromProps({
          user: {
            ...user,
            apartmentLocator: parseInt(apartmentLocator, 10),
          },
        }));
      });
    }

    if (userUpdates.apartmentLeadsSwitchDisabled === true) {
      userUpdates.apartmentLeadsEnabled = false;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'users',
        where: {
          id: user.id,
        },
        updates: {
          ...userUpdates,
          roles: partnerId && partnerDisplayName && !userUpdates.roles.includes(USER_ROLE_PARTNER)
            ? [...userUpdates.roles, USER_ROLE_PARTNER]
            : userUpdates.roles,
        },
      },
    }).then(() => {
      Promise.all([
        dispatch(AdminActions.getUserByPhone, user.phone),
        dispatch(HouseholdActions.getHouseholdByUserPhone, user.phone),
        dispatch(AdminActions.getAssignedHouseholdAdminByPhone, user.phone),
        dispatch(PartnerActions.getByUserId, {
          userId: user.id,
        }),
      ]);
      if (
        (userUpdates.leaseSituation && userUpdates.leaseSituation !== user.leaseSituation)
        || (
          userUpdates.roles.includes(USER_ROLE_LIMITED_TOURS)
          !== user.roles.includes(USER_ROLE_LIMITED_TOURS)
        )
      ) {
        const allLeaseTags = Object.values(leaseSituationTagMap);
        const frontTagsToAdd = [leaseSituationTagMap[userUpdates.leaseSituation]];
        const frontTagsToRemove = allLeaseTags.filter((tag) => !frontTagsToAdd.includes(tag));

        if (userUpdates.roles.includes(USER_ROLE_LIMITED_TOURS)) {
          frontTagsToAdd.push(FRONT_TAG_LIMITED_TOURS);
        }
        else {
          frontTagsToRemove.push(FRONT_TAG_LIMITED_TOURS);
        }

        dispatch(postWithState, {
          action: SR_API_ACTION_UPDATE_RENTER_FRONT_TAGS,
          payload: {
            userId: user.id,
            tagsToAdd: frontTagsToAdd,
            tagsToRemove: frontTagsToRemove,
          },
        });
      }

      dispatch(AdminActions.getAssignedApartmentLocatorByPhone, user.phone).then(() => {
        if (typeof global.alert === 'function') {
          global.alert('User updated!');
        }
      });
    });
  }

  _onResetHouseholdOverviewSettingsClick = () => {
    const { user, dispatch } = this.props;
    if (!global.confirm(
      `Are you sure you want to reset the household overview settings for "${user.name}"?`
    )) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_RESET_HOUSEHOLD_OVERVIEW_SETTINGS,
      payload: {
        userId: user.id,
      },
    });
  }

  render() {
    const {
      userApiState,
      updateUserApiState,
      user,
      apartmentLocator,
      householdId,
      householdAdminUserId,
      apartmentLocators,
      householdAdminsList,
      homebaseEmployeeList,
      resetHouseholdOverviewSettingsApiState,
      marketsById,
      items,
      partners,
      partnersByUserId,
    } = this.props;
    const {
      isDataLoaded,
    } = this.state;
    const lead = items[0];

    const marketList = lodashValues(marketsById);

    return (
      <div className='page-admin'>
        <div className='adminContainer'>
          <Navbar redirectLogoTo='/admin' />

          {(
            <div className='section'>
              <Formik
                initialValues={{ userPhone: '' }}
                onSubmit={this._onLoadUser}
                render={({ values, handleSubmit }) => (
                  <Form>
                    <Field
                      type='text'
                      className='userPhone'
                      name='userPhone'
                      placeholder='User Phone'
                    />
                    <ButtonDeprecated
                      theme={BUTTON_THEME_BLUE}
                      onClick={handleSubmit}
                      disabled={userApiState.inProgress || (user && user.id === values.userPhone)}
                    >
                      {user ? 'Loaded' : 'Load'}
                    </ButtonDeprecated>
                  </Form>
                )}
              />
              {!user && isDataLoaded && (
                <SrErrorMessage>
                  User not found.
                </SrErrorMessage>
              )}
            </div>
          )}

          {user && isDataLoaded && (
            <Formik
              enableReinitialize
              initialValues={createInitialValues({
                formFields: getFormFields({
                  apartmentLocatorsList: apartmentLocators,
                  householdAdminsList: householdAdminsList,
                  homebaseEmployeeList,
                  includeApartmentLocatorFields: true,
                  includeHouseholdAdminFields: true,
                  includeAgentFields: true,
                  lead: lead,
                  householdId: householdId,
                }),
                defaultValues: {
                  ...user,
                  apartmentLocator: apartmentLocator,
                  leadStatus: lead ? lead.status : undefined,
                  householdAdmin: householdAdminUserId || undefined,
                  partnerId: partnersByUserId[user.id]?.id,
                  partnerDisplayName: partnersByUserId[user.id]?.displayName,
                },
              })}
              validateOnChange={false}
              validate={(values) => {
                const formFields = getFormFields({
                  apartmentLocatorsList: apartmentLocators,
                  householdAdminsList: householdAdminsList,
                  homebaseEmployeeList,
                  marketList,
                  includeApartmentLocatorFields: values.roles.includes(USER_ROLE_APARTMENT_LOCATOR),
                  includeHouseholdAdminFields: values.roles.includes(USER_ROLE_HOUSEHOLD_ADMIN),
                  includeAgentFields: values.roles.includes(USER_ROLE_AGENT),
                  lead: lead,
                  householdId: householdId,
                });
                const errors = {};

                formFields.forEach(({ id, validations }) => {
                  if (!validations) {
                    return;
                  }

                  for (const validation of validations) {
                    const validator = validators[validation];
                    if (validator) {
                      const error = validator(values[id]);
                      if (error) {
                        errors[id] = error;
                        break;
                      }
                    }
                  }
                });

                return errors;
              }}
              onSubmit={this._onUpdateUser}
              render={(formikBag) => {
                const formFields = getFormFields({
                  apartmentLocatorsList: apartmentLocators,
                  householdAdminsList: householdAdminsList,
                  homebaseEmployeeList,
                  marketList,
                  includeApartmentLocatorFields: formikBag.values.roles.includes(USER_ROLE_APARTMENT_LOCATOR),
                  includeHouseholdAdminFields: formikBag.values.roles.includes(USER_ROLE_HOUSEHOLD_ADMIN),
                  includeAgentFields: formikBag.values.roles.includes(USER_ROLE_AGENT),
                  lead: lead,
                  householdId: householdId,
                  partners,
                  onPartnerChange: ({ newValue, formikBag }) => {
                    const partner = partners.find(({ displayName }) => displayName === newValue);
                    if (partner) {
                      formikBag.setFieldValue('partnerId', partner.id);
                    }
                  },
                });

                return (
                  <form>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                    ))}
                    {updateUserApiState.error && (
                      <div style={{ color: 'red' }}>
                        {updateUserApiState.error.detail || updateUserApiState.error.message}
                      </div>
                    )}

                    <ButtonDeprecated
                      theme={BUTTON_THEME_BLUE}
                      onClick={formikBag.handleSubmit}
                      disabled={!formikBag.isValid}
                    >
                      {updateUserApiState.inProgress ? 'Updating' : 'Update'}
                    </ButtonDeprecated>
                    <ButtonDeprecated
                      theme={BUTTON_THEME_YELLOW}
                      onClick={this._onResetHouseholdOverviewSettingsClick}
                      disabled={resetHouseholdOverviewSettingsApiState.inProgress}
                    >
                      {resetHouseholdOverviewSettingsApiState.inProgress
                        ? 'Please wait...'
                        : 'Reset Household Overview Settings'
                      }
                    </ButtonDeprecated>
                  </form>
                );
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default AdminUserEditPage;
